import paper from "paper";
import { computeOffsetPathData } from "./constructionOffset";
import { offsetSign } from "./stitchOffset";

/**
 * Geometry for offsetting a leather part / cutout outline (#972, epic #781).
 * Given a closed outline, a distance and which side to go to, produce the
 * pathData of a new closed outline parallel to it.
 *
 * The parallel-curve math is the construction offset engine
 * (`computeOffsetPathData`), and the inward-sign probing is the outline helper
 * extracted for the stitch edge-offset gesture (#768, `stitchOffset.ts`). What
 * this module adds is the piece-specific contract: the RESULT has to be a
 * usable closed outline that actually landed on the requested side. The engine
 * does not report failure for an over-deep inset — past the shape's inradius it
 * emits a self-intersecting fold-back ring that still parses as closed and
 * still spans the source's full bounds. Callers get `null` for that instead, so
 * "offset too deep" is a real, detectable outcome rather than a corrupt part.
 */

/** An offset outline below this area (mm²) has collapsed to a sliver. */
const MIN_AREA_MM2 = 1;

/** How many points to sample per candidate ring when validating. */
const VALIDATE_SAMPLES = 24;

/** A correct offset sits `distance` from the source everywhere. Fold-back
 *  artifacts touch or cross it, so require each sample to clear at least this
 *  fraction of the requested distance — loose enough for the rounding a
 *  corner arc introduces, tight enough to reject an artifact that runs back
 *  along the source edge. */
const MIN_CLEARANCE_RATIO = 0.5;

/** Parse pathData into its subpaths (a lone Path counts as one). */
function subpathsOf(
  pathData: string,
  scope: paper.PaperScope,
): { compound: paper.CompoundPath; subs: paper.Path[] } | null {
  let compound: paper.CompoundPath;
  try {
    compound = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs =
    compound.children && compound.children.length > 0
      ? (compound.children as paper.Path[])
      : [compound as unknown as paper.Path];
  return { compound, subs };
}

/** True when every sampled point of `ring` is on the wanted side of `source`
 *  AND far enough from it to be a genuine offset rather than a fold-back. */
function ringIsValid(
  ring: paper.Path,
  source: paper.CompoundPath,
  distanceMm: number,
  wantInside: boolean,
): boolean {
  if (!ring.length || ring.length <= 0) return false;
  const need = distanceMm * MIN_CLEARANCE_RATIO;
  for (let i = 0; i < VALIDATE_SAMPLES; i++) {
    const pt = ring.getPointAt((ring.length * i) / VALIDATE_SAMPLES);
    if (!pt) return false;
    if (source.contains(pt) !== wantInside) return false;
    const near = source.getNearestPoint(pt);
    if (!near || near.getDistance(pt) < need) return false;
  }
  return true;
}

/**
 * The outline `distanceMm` away from `sourcePathData`, on the inside
 * (`wantInside`) or the outside, as pathData — or null when the offset
 * collapses, folds back, or won't compute.
 *
 * Takes the side as intent rather than a signed distance: winding direction
 * decides which raw sign the offset engine treats as inward, so the sign is
 * probed here (`offsetSign`) instead of being every caller's problem. Only
 * CLOSED, validated rings are kept; when a shape offsets into several disjoint
 * islands all surviving rings are kept, so the result stays a faithful
 * compound outline.
 */
export function offsetOutlinePathData(
  sourcePathData: string,
  distanceMm: number,
  wantInside: boolean,
  scope: paper.PaperScope,
  /** #423: `roundedCorners: false` keeps an outward offset's corners
   *  square instead of arcing them. Ignored for an outline with curves
   *  in it, which has no sharp corners to keep. */
  options?: { roundedCorners?: boolean },
): string | null {
  if (!isFinite(distanceMm) || distanceMm < 1e-6) return null;
  const sign = offsetSign(sourcePathData, scope, null, wantInside);
  const raw = computeOffsetPathData(
    sourcePathData,
    sign * distanceMm,
    scope,
    options,
  );
  if (!raw) return null;
  const src = subpathsOf(sourcePathData, scope);
  const parsed = subpathsOf(raw, scope);
  if (!src || !parsed) {
    src?.compound.remove();
    parsed?.compound.remove();
    return null;
  }
  const keep = parsed.subs.filter(
    (s) =>
      s &&
      s.closed &&
      Math.abs(s.area) >= MIN_AREA_MM2 &&
      ringIsValid(s, src.compound, distanceMm, wantInside),
  );
  let data: string | null = null;
  if (keep.length > 0) {
    const out = new scope.CompoundPath({ insert: false });
    for (const s of keep)
      out.addChild(s.clone({ insert: false }) as paper.Path);
    data = out.pathData || null;
    out.remove();
  }
  src.compound.remove();
  parsed.compound.remove();
  return data;
}
